import type {
  DiscoveryArea,
  DiscoveryAreaPhase,
  DiscoveryIntelligenceProgressDto,
  DiscoverySummaryDto,
  OpportunityAreaDetailDto,
  RelevantEmployeeProfileDto,
} from '../../features/opportunities/opportunity-wizard/steps/smart-discovery/models/discovery-intelligence.models';
import { getDiscoveryMockTiming } from './discovery-intelligence-mock.timing';
import { mockEnqueueScotsmanAnalysisAfterDiscoveryFinalize } from './scotsman-api-mock.handlers';
import { mockMarkDiscoveryFinalized } from './qualification-wizard-mock.handlers';

const MOCK_RUN_ID = 'mock-discovery-run-active';
const MOCK_RUN_COMPLETED_ID = 'mock-discovery-run-completed';
const MOCK_WORKSPACE_ID = 'mock-workspace-id';
const MOCK_ASSESSMENT_RUN_ID = 'mock-assessment-run-001';

const INSIGHT_PROFILE_BASE = 'https://insight.zuehlke.com/employees/aiepg/';

const MOCK_RELEVANT_PROFILES: RelevantEmployeeProfileDto[] = [
  {
    url: `${INSIGHT_PROFILE_BASE}a1b2c3d4-e5f6-7890-abcd-ef1234567890`,
    reason: 'HasSkill',
    title: 'Senior Cloud Engineer',
    location: 'Zürich',
    role: 'Software Architect',
  },
  {
    url: `${INSIGHT_PROFILE_BASE}b2c3d4e5-f6a7-8901-bcde-f12345678901`,
    reason: 'HasSkill',
    title: 'Lead Azure Consultant',
    location: 'Vienna',
    role: 'Solution Architect',
  },
  {
    url: `${INSIGHT_PROFILE_BASE}c3d4e5f6-a7b8-9012-cdef-123456789012`,
    reason: 'WorkedOnRelatedProject',
    title: 'Principal Engineer',
    location: 'Frankfurt',
    role: 'Platform Engineer',
    projectTitle: 'Core Banking Platform Migration',
    projectCode: 'BANK-142',
  },
  {
    url: `${INSIGHT_PROFILE_BASE}d4e5f6a7-b8c9-0123-defa-234567890123`,
    reason: 'ProfileBackground',
    title: 'Solution Architect',
    location: 'Zürich',
    role: 'Cloud Architect',
  },
];

const UI_AREAS: readonly DiscoveryArea[] = ['Scope', 'Capability', 'Market', 'Motivation'];

const INTELLIGENCE_BASE_PATH = /^\/api\/opportunities\/([^/]+)\/smart-discovery\/intelligence/;
const INTELLIGENCE_ANALYZE_PATH =
  /^\/api\/opportunities\/([^/]+)\/smart-discovery\/intelligence\/analyze$/;
const INTELLIGENCE_FINALIZE_PATH =
  /^\/api\/opportunities\/([^/]+)\/smart-discovery\/intelligence\/finalize$/;
const INTELLIGENCE_AREA_PATH =
  /^\/api\/opportunities\/[^/]+\/smart-discovery\/intelligence\/areas\/([^/]+)$/;

export interface DiscoveryIntelligenceMockHandlerResult {
  status: number;
  body: unknown;
  delayMs?: number;
}

interface OpportunityMockState {
  progressPollCount: number;
}

const stateByOpportunityId = new Map<string, OpportunityMockState>();
const completedByOpportunityId = new Set<string>();

function getOrCreateState(opportunityId: string): OpportunityMockState {
  let state = stateByOpportunityId.get(opportunityId);
  if (!state) {
    state = { progressPollCount: 0 };
    stateByOpportunityId.set(opportunityId, state);
  }
  return state;
}

function prepareForProgressGet(opportunityId: string): OpportunityMockState {
  const timing = getDiscoveryMockTiming();
  const state = getOrCreateState(opportunityId);

  if (timing.replayOnEachBind && completedByOpportunityId.has(opportunityId)) {
    state.progressPollCount = 0;
    completedByOpportunityId.delete(opportunityId);
  }

  return state;
}

function areaPhaseForPoll(areaIndex: number, pollCount: number): DiscoveryAreaPhase {
  const timing = getDiscoveryMockTiming();
  if (pollCount >= timing.signalsExtractingProgressPolls) {
    return 'Idle';
  }
  if (pollCount > areaIndex) {
    return 'Idle';
  }
  if (pollCount === areaIndex + 1) {
    return 'Running';
  }
  return 'Pending';
}

function mockProgressForPoll(
  opportunityId: string,
  pollCount: number,
): DiscoveryIntelligenceProgressDto {
  const timing = getDiscoveryMockTiming();
  const now = new Date().toISOString();
  const isComplete = pollCount >= timing.summaryReadyAfterProgressPolls;
  const isExtracting = pollCount < timing.signalsExtractingProgressPolls;
  const isSummaryStage = !isExtracting && !isComplete;

  if (isComplete) {
    return {
      workspaceId: MOCK_WORKSPACE_ID,
      opportunityId,
      phase: 'Idle',
      areas: UI_AREAS.map((area) => ({
        area,
        phase: 'Idle',
        currentStage: null,
        startedAtUtc: now,
        updatedAtUtc: now,
        assessmentRunId: MOCK_ASSESSMENT_RUN_ID,
      })),
      activeRun: null,
      lastCompletedRun: {
        id: MOCK_RUN_COMPLETED_ID,
        status: 'Completed',
        startedAtUtc: now,
        completedAtUtc: now,
      },
    };
  }

  return {
    workspaceId: MOCK_WORKSPACE_ID,
    opportunityId,
    phase: isSummaryStage || isExtracting ? 'Running' : 'Idle',
    areas: UI_AREAS.map((area, index) => ({
      area,
      phase: areaPhaseForPoll(index, pollCount),
      currentStage: isExtracting ? 'ExtractingSignals' : isSummaryStage ? 'Summarizing' : null,
      startedAtUtc: now,
      updatedAtUtc: now,
      assessmentRunId: MOCK_ASSESSMENT_RUN_ID,
    })),
    activeRun: {
      id: MOCK_RUN_ID,
      status: 'Running',
      startedAtUtc: now,
      completedAtUtc: null,
    },
    lastCompletedRun: null,
  };
}

function mockSummary(opportunityId: string): DiscoverySummaryDto {
  return {
    id: 'mock-discovery-summary',
    workspaceId: MOCK_WORKSPACE_ID,
    opportunityId,
    title: 'Core application for the Canton of Bern',
    leadMarkdown:
      '**Canton of Bern** is procuring a core application via open procedure on simap.ch.',
    dealEvents: [
      {
        dateText: '15.08.2026',
        date: '2026-08-15',
        label: 'Publication on simap.ch',
        expected: false,
      },
      {
        dateText: '28.08.2026',
        date: '2026-08-28',
        label: 'Deadline for questions about the tender documents',
        expected: false,
      },
      {
        dateText: '03.09.2026',
        date: '2026-09-03',
        label: 'Customer responses to tender questions',
        expected: false,
      },
      {
        dateText: '24.09.2026 18:00',
        date: '2026-09-24',
        label: 'Legally signed offer submission deadline',
        expected: false,
      },
      { dateText: '25.09.2026', date: '2026-09-25', label: 'Offer opening', expected: false },
      { dateText: '09.11.2026', date: '2026-11-09', label: 'Bidder presentation', expected: false },
      { dateText: 'CW 49 2026', date: null, label: 'Contract award', expected: true },
      { dateText: 'before CW 01 2027', date: null, label: 'Contract conclusion', expected: true },
    ],
    dealProse: '',
    deliveryMarkdown: `| Phase / Milestone | Target | Notes |
|-------------------|--------|-------|
| LP1–LP3 core concept and implementation | 01.01.2027–30.11.2027 | |
| LP4 output-management implementation | 01.01.2027–30.11.2027 | optional, parallel with LP2 |
| LP5 migration and testing | 01.01.2027–30.11.2027 | parallel with LP2 |
| Go-live | 30.11.2027 | |
| LP6 core-application operations | 01.12.2027–30.11.2030 | |
| LP4a output-management operations | 01.12.2027–30.11.2030 | optional |
| LP7 core-operations extension | 01.12.2030–30.11.2040 | optional |
| LP4b output-management extension | 01.12.2030–maximum 30.11.2040 | optional |
| LP8 advisory, development, training and end-of-life services | 01.01.2027–30.11.2040 | optional |`,
    awardMarkdown: `Highest-scoring offer wins. Evidenced weights 60%.

- **Price** 25%
- **ZK04 Project approach and organization for LP1–LP4** 15%
- **Innovation** 10%
- **ZK06 Presentation/demo** 10% (1,000 pts)`,
    summaryMarkdown: `# Core application for the Canton of Bern (simap)

**Canton of Bern** is procuring a core application via open procedure on simap.ch.

---

**Deal timeline:**
- **15.08.2026** Publication on simap.ch
- **28.08.2026** Deadline for questions about the tender documents
- **03.09.2026** Customer responses to tender questions
- **24.09.2026 18:00** Legally signed offer submission deadline
- **25.09.2026** Offer opening
- **09.11.2026** Bidder presentation
- **~ CW 49 2026** Contract award
- **~ before CW 01 2027** Contract conclusion

---

**Delivery timeline:**
| Phase / Milestone | Target | Notes |
|-------------------|--------|-------|
| LP1–LP3 core concept and implementation | 01.01.2027–30.11.2027 | |
| LP4 output-management implementation | 01.01.2027–30.11.2027 | optional, parallel with LP2 |
| LP5 migration and testing | 01.01.2027–30.11.2027 | parallel with LP2 |
| Go-live | 30.11.2027 | |
| LP6 core-application operations | 01.12.2027–30.11.2030 | |
| LP4a output-management operations | 01.12.2027–30.11.2030 | optional |
| LP7 core-operations extension | 01.12.2030–30.11.2040 | optional |
| LP4b output-management extension | 01.12.2030–maximum 30.11.2040 | optional |
| LP8 advisory, development, training and end-of-life services | 01.01.2027–30.11.2040 | optional |

---

**Award:**
Highest-scoring offer wins. Evidenced weights 60%.

- **Price** 25%
- **ZK04 Project approach and organization for LP1–LP4** 15%
- **Innovation** 10%
- **ZK06 Presentation/demo** 10% (1,000 pts)
`,
    proposalSummary: 'Strong Azure capability coverage with identifiable delivery references.',
    topClarifications: [
      {
        question:
          'Should this engagement be structured as a product team with a fixed outcome, or as a time-and-materials delivery supporting your internal product org?',
        area: 'Scope',
        priority: 'High',
        clarificationRef: 'mock-scope-clarification-delivery-model',
      },
      {
        question:
          'What are the mandatory milestone dates between kick-off and go-live, and which are fixed vs. movable?',
        area: 'Scope',
        priority: 'High',
        clarificationRef: 'mock-scope-clarification-timeline',
      },
    ],
    areaSnapshots: UI_AREAS.map((area) => ({
      area,
      status: area === 'Capability' ? 'Green' : 'Amber',
      headline:
        area === 'Capability'
          ? 'Insight profiles demonstrate strong cloud platform coverage.'
          : `${area} assessed in mock mode.`,
      findingCount: area === 'Capability' ? 1 : area === 'Scope' ? 2 : 0,
      clarificationCount: area === 'Scope' ? 3 : 0,
    })),
    assessmentRunId: MOCK_ASSESSMENT_RUN_ID,
    generatedByService: 'mock',
    generatedByVersion: '1',
    createdAtUtc: '2026-06-08T10:00:00.000Z',
  };
}

function mockCapabilityAreaDetail(): OpportunityAreaDetailDto {
  const signalAzure = 'mock-sig-azure-platform';
  const signalTerraform = 'mock-sig-terraform';

  return {
    area: 'Capability',
    status: 'Green',
    summary:
      'Insight profiles demonstrate strong cloud platform coverage for the requirements below.',
    findings: [
      {
        id: 'mock-cap-finding-azure-coverage',
        area: 'Capability',
        findingKind: 'Strength',
        severity: 'Green',
        status: 'Active',
        category: 'Cloud platform',
        title: 'Strong Azure and platform engineering coverage',
        summary:
          'Multiple Insight profiles hold proficient Azure and Terraform skills aligned with the RFP stack. Delivery references include regulated cloud migrations in DACH.',
        rationale: null,
        signalRefs: [signalAzure, signalTerraform],
        knowledgeRefs: [],
        assessmentRunId: MOCK_ASSESSMENT_RUN_ID,
        evidence: [],
        relevantEmployeeProfiles: MOCK_RELEVANT_PROFILES,
        searchSkillUrl: 'https://insight.zuehlke.com/skills/skill-azure-cloud/community',
      },
    ],
    clarifications: [],
    signals: [
      {
        id: signalAzure,
        area: 'Capability',
        signalKind: 'Capability',
        signalType: 'Skill',
        signalSubType: null,
        value: 'Microsoft Azure',
        normalizedValue: 'Azure',
        confidence: 0.92,
        status: 'Active',
        assessmentRunId: MOCK_ASSESSMENT_RUN_ID,
        evidence: [],
      },
      {
        id: signalTerraform,
        area: 'Capability',
        signalKind: 'Capability',
        signalType: 'Skill',
        signalSubType: null,
        value: 'Terraform',
        normalizedValue: 'Terraform',
        confidence: 0.88,
        status: 'Active',
        assessmentRunId: MOCK_ASSESSMENT_RUN_ID,
        evidence: [],
      },
    ],
  };
}

function mockScopeAreaDetail(): OpportunityAreaDetailDto {
  const findingDeliveryModel = 'mock-scope-finding-delivery-model';
  const findingTimeline = 'mock-scope-finding-timeline';

  return {
    area: 'Scope',
    status: 'Amber',
    summary:
      'Scope dimensions are partially defined; buyer clarification is needed on delivery model and timeline.',
    findings: [
      {
        id: findingDeliveryModel,
        area: 'Scope',
        findingKind: 'Ambiguity',
        severity: 'Amber',
        status: 'Active',
        category: 'Delivery model',
        title: 'Product vs. project engagement unclear',
        summary:
          'RFP language mixes product roadmap ownership with time-and-materials delivery, without stating the intended engagement model.',
        rationale: null,
        signalRefs: [],
        knowledgeRefs: [],
        assessmentRunId: MOCK_ASSESSMENT_RUN_ID,
        evidence: [],
      },
      {
        id: findingTimeline,
        area: 'Scope',
        findingKind: 'Gap',
        severity: 'Red',
        status: 'Active',
        category: 'Timeline',
        title: 'Go-live date not anchored to milestones',
        summary:
          'A target go-live is mentioned but no phased milestones or decision gates are defined.',
        rationale: null,
        signalRefs: [],
        knowledgeRefs: [],
        assessmentRunId: MOCK_ASSESSMENT_RUN_ID,
        evidence: [],
      },
    ],
    clarifications: [
      {
        id: 'mock-scope-clarification-delivery-model',
        area: 'Scope',
        question:
          'Should this engagement be structured as a product team with a fixed outcome, or as a time-and-materials delivery supporting your internal product org?',
        rationale:
          'The RFP references both roadmap ownership and staff augmentation; the answer changes team shape, governance, and pricing.',
        audience: 'Customer',
        status: 'Open',
        sourceFindingId: findingDeliveryModel,
      },
      {
        id: 'mock-scope-clarification-timeline',
        area: 'Scope',
        question:
          'What are the mandatory milestone dates between kick-off and go-live, and which are fixed vs. movable?',
        rationale:
          'Without milestone anchors we cannot assess feasibility or propose a credible delivery plan.',
        audience: 'Customer',
        status: 'Open',
        sourceFindingId: findingTimeline,
      },
      {
        id: 'mock-scope-clarification-internal-staffing',
        area: 'Scope',
        question:
          'Do we already have a named delivery lead and architect allocated for the pursuit phase?',
        rationale:
          'Internal resourcing affects whether we can commit to the proposed timeline in the response.',
        audience: 'Internal',
        status: 'Dismissed',
        sourceFindingId: findingTimeline,
      },
    ],
    signals: [],
  };
}

function mockGenericAreaDetail(area: DiscoveryArea): OpportunityAreaDetailDto {
  return {
    area,
    status: 'Amber',
    summary: `${area} assessment stub for mock mode.`,
    findings: [],
    clarifications: [],
    signals: [],
  };
}

/**
 * Simulates discovery intelligence progress polling: area extraction → summary → complete.
 */
export function mockHandleDiscoveryIntelligenceGet(
  canonical: string,
): DiscoveryIntelligenceMockHandlerResult | null {
  const baseMatch = INTELLIGENCE_BASE_PATH.exec(canonical);
  if (!baseMatch) {
    return null;
  }

  const opportunityId = decodeURIComponent(baseMatch[1]);
  const timing = getDiscoveryMockTiming();
  const state = getOrCreateState(opportunityId);

  if (canonical.endsWith('/progress')) {
    prepareForProgressGet(opportunityId);
    state.progressPollCount += 1;

    if (state.progressPollCount >= timing.summaryReadyAfterProgressPolls) {
      completedByOpportunityId.add(opportunityId);
    }

    return {
      status: 200,
      body: mockProgressForPoll(opportunityId, state.progressPollCount),
      delayMs: timing.pollResponseDelayMs,
    };
  }

  if (canonical.endsWith('/summary')) {
    if (state.progressPollCount < timing.summaryReadyAfterProgressPolls) {
      return { status: 404, body: null, delayMs: timing.pollResponseDelayMs };
    }
    return {
      status: 200,
      body: mockSummary(opportunityId),
      delayMs: timing.pollResponseDelayMs,
    };
  }

  const areaMatch = INTELLIGENCE_AREA_PATH.exec(canonical);
  if (areaMatch) {
    const area = decodeURIComponent(areaMatch[1]) as DiscoveryArea;
    if (!UI_AREAS.includes(area)) {
      return { status: 404, body: null, delayMs: timing.pollResponseDelayMs };
    }
    const body =
      area === 'Capability'
        ? mockCapabilityAreaDetail()
        : area === 'Scope'
          ? mockScopeAreaDetail()
          : mockGenericAreaDetail(area);
    return { status: 200, body, delayMs: timing.pollResponseDelayMs };
  }

  return null;
}

/** Restarts mock discovery extraction (same behaviour as a fresh upload bind). */
export function mockHandleDiscoveryIntelligenceAnalyze(
  canonical: string,
): DiscoveryIntelligenceMockHandlerResult | null {
  const match = INTELLIGENCE_ANALYZE_PATH.exec(canonical);
  if (!match) {
    return null;
  }

  const opportunityId = decodeURIComponent(match[1]);
  restartMockDiscoveryAnalysis(opportunityId);

  return {
    status: 202,
    body: { correlationId: `mock-discovery-${opportunityId}` },
  };
}

/** Finalizes discovery step — downstream mock enqueues discovery analyze when needed. */
export function mockHandleDiscoveryIntelligenceFinalize(
  canonical: string,
): DiscoveryIntelligenceMockHandlerResult | null {
  const match = INTELLIGENCE_FINALIZE_PATH.exec(canonical);
  if (!match) {
    return null;
  }

  const opportunityId = decodeURIComponent(match[1]);
  mockEnqueueScotsmanAnalysisAfterDiscoveryFinalize(opportunityId);
  mockMarkDiscoveryFinalized(opportunityId);

  return {
    status: 200,
    body: { correlationId: `mock-discovery-finalize-${opportunityId}` },
  };
}

function restartMockDiscoveryAnalysis(opportunityId: string): void {
  const state = getOrCreateState(opportunityId);
  state.progressPollCount = 0;
  completedByOpportunityId.delete(opportunityId);
}

export function mockRestartDiscoveryAnalysisForUploadFinalize(opportunityId: string): void {
  restartMockDiscoveryAnalysis(opportunityId);
}
